import React, { useRef } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { UploadFileButton } from 'Modules/Reimbursements/FamilyMembers/UploadFileButton';
import {
  checkFileSize,
  isVideoType,
} from 'Modules/UniversalRequest/FileUpload/Utils';
import { isFileValid } from 'Utils/isFileValid';
import { ALLOWED_VIDEO_TYPES } from 'Config';

interface IUploadFileArea {
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  isTypeError: boolean;
  setIsTypeError: (value: boolean) => void;
  setRecordedBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
}

export const UploadNativeRecording = ({
  setFile,
  isTypeError,
  setIsTypeError,
  setRecordedBlob,
}: IUploadFileArea) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Feedback).uploadArea;
  const fileInput = useRef<HTMLInputElement[]>([]);

  const handleFile = async (file: File) => {
    if (checkFileSize(file) && isVideoType(file)) {
      if (!(await isFileValid(file))) {
        setIsTypeError(true);
        return;
      }
      setIsTypeError(false);
      setFile(file);
      setRecordedBlob(file);
    } else {
      setIsTypeError(true);
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file !== null) {
        handleFile(file);
        event.target.value = '';
      }
    }
  };

  const clickFileInput = (index: number) => {
    if (fileInput.current) {
      fileInput.current[index].click();
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: 'primary.shade_50',
        borderRadius: '16px',
        mt: '-24px',
      }}
    >
      <Box>
        <input
          ref={(e) => {
            fileInput.current[0] = e!;
          }}
          type='file'
          accept={ALLOWED_VIDEO_TYPES}
          hidden
          onChange={(e) => onChangeHandler(e)}
          id={`upload-webm-file`}
          aria-labelledby={isTypeError ? 'upload-file-error' : undefined}
        />
        <label htmlFor={`upload-file`} className='sr-only'>
          `${dictionary.uploadVideoFile}`
        </label>

        <UploadFileButton clickHandler={() => clickFileInput(0)}>
          {dictionary.uploadVideoFile}
        </UploadFileButton>
      </Box>
      {isTypeError && (
        <Typography
          id='upload-file-error'
          role='alert'
          aria-live='assertive'
          variant='body6'
          sx={{
            color: '#E12D2D',
            pl: '16px',
            maxWidth: '600px',
            mt: '6px',
          }}
        >
          {dictionary.typeError}
        </Typography>
      )}
    </Stack>
  );
};
